import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth
export const exchangeSession = (sessionId) =>
  api.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } });
export const fetchMe = () => api.get("/auth/me");
export const logout = () => api.post("/auth/logout");
// V2.0.54 — self-serve account deletion. Removes the user + every
// owned design, unlock, render, referral record, lookup, feedback,
// and active session. Irreversible.
export const deleteMyAccount = () => api.delete("/auth/me");
// V2.0.55 — pre-flight counts shown in the final "are you ABSOLUTELY
// sure?" dialog.
export const previewAccountDeletion = () => api.get("/auth/me/deletion-preview");

// Designs
export const listDesigns = () => api.get("/designs");
export const createDesign = (data) => api.post("/designs", data);
export const getDesign = (id) => api.get(`/designs/${id}`);
export const updateDesign = (id, data) => api.put(`/designs/${id}`, data);
export const deleteDesign = (id) => api.delete(`/designs/${id}`);

// V2.0.85 — Upload a floor-plan file (image / PDF / CAD) and let the
// backend run it through Gemini vision to produce a parsed blueprint
// preview. V2.0.91 — Now consumes NDJSON stream so long PDF parses
// don't hit Cloudflare's 100s edge timeout (524). Backwards compatible
// on the wire — heartbeats keep bytes flowing while Gemini works.
//
// Options:
//   floorIndex   — integer 0/1/2… tag every parsed room with this floor
//   onHeartbeat  — callback fired ~every 10s so the UI can show progress
//
// Return shape mirrors sendChat: `{ data: <upload result payload> }`.
export const uploadDesignSource = (id, file, opts = {}) => {
  const form = new FormData();
  form.append("file", file);
  if (Number.isInteger(opts.floorIndex) && opts.floorIndex >= 0) {
    form.append("floor_index", String(opts.floorIndex));
  }
  const url = `${API}/designs/${id}/upload-source`;
  return (async () => {
    const resp = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/x-ndjson" },
      body: form,
    });
    if (!resp.ok) {
      let detail = `Upload failed (${resp.status})`;
      try {
        const text = await resp.text();
        try { detail = JSON.parse(text).detail || text; } catch { detail = text || detail; }
      } catch { /* ignore */ }
      const err = new Error(detail);
      err.response = { status: resp.status, data: { detail } };
      throw err;
    }
    if (!resp.body) throw new Error("Streaming not supported in this browser");
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const MAX_BUFFER_CHARS = 16 * 1024 * 1024;
    const onHeartbeat = typeof opts.onHeartbeat === "function" ? opts.onHeartbeat : null;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      if (buffer.length > MAX_BUFFER_CHARS) {
        throw new Error("Upload stream exceeded 16M-char safety cap.");
      }
      let idx;
      while ((idx = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line) continue;
        let evt;
        try { evt = JSON.parse(line); } catch { continue; }
        if (!evt || typeof evt !== "object") continue;
        if (evt.event === "result") return { data: evt.data };
        if (evt.event === "error") {
          const status = evt.status || 500;
          const detail = evt.detail || "Upload failed";
          const err = new Error(detail);
          err.response = { status, data: { detail } };
          throw err;
        }
        if (evt.event === "heartbeat" && onHeartbeat) {
          try { onHeartbeat(evt); } catch { /* never break the read loop */ }
        }
      }
    }
    throw new Error("Upload stream ended without a result");
  })();
};

// V2.0.89 — Toggle whether a shared design appears in /gallery and
// /best-transformations. Body: {discoverable: true|false}.
export const setDesignDiscoverable = (id, discoverable) =>
  api.put(`/designs/${id}/discoverable`, { discoverable });

// Chat
// V2.0.66 — Streaming NDJSON consumer. The backend now returns one JSON
// object per line so it can emit `{"event":"heartbeat"}` lines every ~10s
// while the chat pipeline runs. This bypasses Cloudflare's 100s edge
// timeout (524) on the production 0.5 vCPU Launch Tier. The return shape
// is still `{ data: <payload> }` so existing callers (Editor.jsx) keep
// working without changes.
export const sendChat = async (id, message, opts = {}) => {
  const url = `${API}/designs/${id}/chat`;
  const body = JSON.stringify({
    message,
    low_detail: !!opts.lowDetail,
    // V1.7 — Simple Mode default ON. Backend swaps to the tight prompt that
    // returns < 2KB payloads instead of full mansion BIM.
    simple_mode: opts.simpleMode !== false,
  });
  const resp = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/x-ndjson" },
    body,
  });
  if (!resp.ok) {
    // Pre-stream HTTP error (404 design-not-found, 401 unauth, etc).
    let detail = `Chat failed (${resp.status})`;
    try {
      const text = await resp.text();
      try { detail = JSON.parse(text).detail || text; } catch { detail = text || detail; }
    } catch { /* ignore */ }
    const err = new Error(detail);
    err.response = { status: resp.status, data: { detail } };
    throw err;
  }
  if (!resp.body) {
    throw new Error("Streaming not supported in this browser");
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // V2.0.71 — Hard cap on unparsed buffer size. Without this, a stream
  // that emits an unterminated line (network hiccup, proxy corruption)
  // would grow the buffer forever until the tab OOMs. 16M UTF-16 code
  // units is well above any legitimate `result` payload; the largest
  // observed mansion-tier blueprint_json is < 400KB. Note: JS `.length`
  // is chars not bytes, but the ratio is ~1:1 for NDJSON so it's a
  // fine proxy.
  const MAX_BUFFER_CHARS = 16 * 1024 * 1024;
  // Surface heartbeats to an optional callback so the UI can keep a
  // "still working…" indicator alive on heavy prompts.
  const onHeartbeat = typeof opts.onHeartbeat === "function" ? opts.onHeartbeat : null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    if (buffer.length > MAX_BUFFER_CHARS) {
      throw new Error(
        "Chat stream exceeded 16M-char safety cap without emitting a " +
          "result line — network or proxy likely corrupted the response."
      );
    }
    let idx;
    while ((idx = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 1);
      if (!line) continue;
      let evt;
      try {
        evt = JSON.parse(line);
      } catch (parseErr) {
        // V2.0.71 — Malformed NDJSON line: log for observability and
        // move on. Previously an in-loop throw here would black-screen
        // the whole editor because the caller never expected `sendChat`
        // to throw a SyntaxError.
        console.warn("[sendChat] dropped malformed NDJSON line:", parseErr?.message);
        continue;
      }
      if (!evt || typeof evt !== "object") continue;
      if (evt.event === "result") {
        return { data: evt.data };
      }
      if (evt.event === "error") {
        const status = evt.status || 500;
        const detail = evt.detail || "Chat failed";
        const err = new Error(detail);
        err.response = { status, data: { detail } };
        throw err;
      }
      if (evt.event === "heartbeat" && onHeartbeat) {
        try { onHeartbeat(evt); } catch { /* never break the read loop */ }
      }
    }
  }
  throw new Error("Chat stream ended without a result");
};

// Sign-off
export const requestSignoff = (id, thumbnailDataUrl, reviewerEmail = null) =>
  api.post(`/designs/${id}/signoff`, {
    thumbnail_data_url: thumbnailDataUrl,
    reviewer_email: reviewerEmail || undefined,
  });

export const saveThumbnail = (id, thumbnailDataUrl) =>
  api.post(`/designs/${id}/thumbnail`, {
    thumbnail_data_url: thumbnailDataUrl,
  });

// V1.7.4 — Paid architectural floor-plan PDF.
export const requestArchitecturalPdf = (id) =>
  api.post(`/designs/${id}/architectural-pdf`);

// V1.7.6 — free preview of the architectural PDF (watermarked, no credit).
export const previewArchitecturalPdf = (id) =>
  api.get(`/designs/${id}/architectural-pdf/preview`);

// V2.0.14 — IFC 4 BIM export (Revit / ArchiCAD / Vectorworks / etc.)
export const requestIfcExport = (id) =>
  api.post(`/designs/${id}/ifc`);
export const previewIfcExport = (id) =>
  api.get(`/designs/${id}/ifc/preview`);

// V1.7.7 — Share, snapshot, public read-only fetch.
export const createShareLink = (id) => api.post(`/designs/${id}/share`);
export const duplicateDesign = (id) => api.post(`/designs/${id}/duplicate`);
export const fetchSharedDesign = (slug) => api.get(`/share/${slug}`);

// V1.9.5 — residential code-compliance scan (Studio Tier).
export const getCodeViolations = (id) =>
  api.get(`/designs/${id}/code-violations`);

// V1.9.6 — energy score (envelope kWh/year + 0..100 rating).
export const getEnergyScore = (id) =>
  api.get(`/designs/${id}/energy-score`);

// V1.7.5 — fetch the persisted AI render gallery without consuming a credit.
export const getDesignRenders = (id) =>
  api.get(`/designs/${id}/renders`);

// V1.7.3 — promote any image (AI render, snapshot) to the dashboard cover.
export const setDesignCover = (id, dataUrl) =>
  api.post(`/designs/${id}/cover`, { data_url: dataUrl });

// Payments
export const listPackages = () => api.get("/payments/packages");
export const startCheckout = (designId, packId = "concept") =>
  api.post("/payments/checkout/session", {
    origin_url: window.location.origin,
    design_id: designId,
    pack_id: packId,
  });
export const checkoutStatus = (sessionId) =>
  api.get(`/payments/checkout/status/${sessionId}`);
export const getCredits = () => api.get("/me/credits");
export const getIntegrationsStatus = () => api.get("/integrations/status");

// V2.0.34 — Project-Unlock pricing (Tier 1 $29 / Tier 2 $99-$299+).
// Replaces the credit funnel for the foundational PDF + IFC + audit-zip
// deliverables. The legacy credit packs above coexist for ad-hoc AI
// Render generation.
export const listPricingTiers = () => api.get("/pricing/tiers");
export const getUnlockQuote = (designId, tierId = null) =>
  api.get(`/designs/${designId}/unlock/quote`, {
    params: tierId ? { tier_id: tierId } : {},
  });
export const startUnlockCheckout = (designId, tierId = null) =>
  api.post(`/designs/${designId}/unlock/checkout`, {
    origin_url: window.location.origin,
    tier_id: tierId,
  });
export const mockUnlockSuccess = (designId, tierId) =>
  api.post(`/dev/project-unlock/mock-success`, {
    design_id: designId,
    tier_id: tierId,
  });
export const validateIfcPreflight = (designId) =>
  api.get(`/designs/${designId}/ifc/validate`);
export const shareDesignWithArchitect = (designId, payload) =>
  api.post(`/designs/${designId}/share-with-architect`, payload);
export const trackReferralVisit = (payload) =>
  api.post(`/referrals/track-visit`, payload);
export const attributeReferral = (referralId) =>
  api.post(`/referrals/attribute`, { referral_id: referralId });
export const redeemReferralTier1 = (designId) =>
  api.post(`/referrals/redeem-tier1`, { design_id: designId });

// Tour / Reel export
export const exportReel = (designId, frames, durationMs = 100, maxWidth) =>
  api.post(
    `/designs/${designId}/export-reel`,
    { frames, duration_ms: durationMs, ...(maxWidth ? { max_width: maxWidth } : {}) },
    { timeout: 120000 },
  );

// Dev-only: mock a successful Stripe payment (disabled in production)
export const mockPaymentSuccess = (packageId = "concept") =>
  api.post("/dev/mock-payment-success", { package_id: packageId });

// Geocoding (server-side proxy to OSM Nominatim) — backend caches in Mongo.
// Passes optional city+state so PO-Box-only ZIPs still resolve.
export const geocodeZip = (zip, country = "US", city = "", state = "") =>
  api.get("/geocode/zip", { params: { zip, country, city, state } });


// V1.8.0 — Analytics (counters + recent events per design).
export const postAnalyticsEvent = (payload) =>
  api.post("/analytics/event", payload);
export const getDesignAnalytics = (designId) =>
  api.get(`/designs/${designId}/analytics`);


// V1.9.0 — Beta launch: feedback + admin + welcome modal
export const submitFeedback = (payload) => api.post("/feedback", payload);
export const getBetaStatus = () => api.get("/me/beta-status");
export const markWelcomeSeen = () => api.post("/me/welcome-seen");
export const listAdminUsers = () => api.get("/admin/users");
export const listAdminFeedback = (status) =>
  api.get("/admin/feedback", { params: status ? { status } : {} });
export const updateFeedbackStatus = (feedbackId, status) =>
  api.patch(`/admin/feedback/${feedbackId}`, { status });
export const sendDigestNow = () => api.post("/admin/send-digest-now");
// V2.0.62c — Stripe Dashboard shortcut. Optional `sessionId` deep-links
// straight to a specific Checkout Session page in the right mode (test/live).
export const getStripeDashboardLink = (sessionId) =>
  api.get("/admin/stripe/dashboard-link", {
    params: sessionId ? { session_id: sessionId } : {},
  });
// V2.0.62d — Resend Dashboard shortcut. Optional `emailId` deep-links
// to a specific email's delivery log.
export const getResendDashboardLink = (emailId) =>
  api.get("/admin/resend/dashboard-link", {
    params: emailId ? { email_id: emailId } : {},
  });
// V2.0.62e — MongoDB Atlas Dashboard shortcut.
export const getMongoDashboardLink = () => api.get("/admin/mongo/dashboard-link");
// V2.0.62f — Aggregated mode/health for all three external services.
export const getServicesStatus = () => api.get("/admin/services/status");
// V2.0.65 — Chat latency dashboard. `hours` capped server-side at 1..168.
export const getChatStats = (hours = 24) =>
  api.get("/admin/chat-stats", { params: { hours } });

// V2.0.72 — Admin feature flags. Read/write the on/off toggles that
// gate optional server-side behaviour (currently just the prompt
// clarification layer).
export const listAdminFlags = () => api.get("/admin/flags");
export const setAdminFlag = (key, value) =>
  api.post(`/admin/flags/${encodeURIComponent(key)}`, { value: !!value });

// V2.0.73 — Client-error viewer for the admin dashboard. Returns
// {recent, grouped, first_attempt_success_rate, ...}. Backed by the
// /api/admin/client-errors endpoint.
export const getClientErrors = (hours = 24, limit = 100) =>
  api.get("/admin/client-errors", { params: { hours, limit } });

// V2.0.81 — Interaction telemetry heatmap (tool usage in the editor).
export const getInteractionEvents = (hours = 168) =>
  api.get("/admin/interaction-events", { params: { hours } });

// V2.0.83 — Prompt suggestions carousel (top-N most-committed prompt
// shapes from the last 7d, falls back to curated defaults if telemetry
// is thin).
export const getPromptSuggestions = (limit = 5, hours = 168) =>
  api.get("/prompt-suggestions", { params: { limit, hours } });


// V2.0.11 — Local Code Finder (lead magnet)
export const lookupLocalCode = (zip, state) =>
  api.get("/code-finder/lookup", { params: { zip, ...(state ? { state } : {}) } });
export const emailCodeReport = (payload) =>
  api.post("/code-finder/email-report", payload);
export const lookupLocalCodeForDesign = (designId) =>
  api.get(`/me/code-finder/${designId}`);


// V2.0.20 — Terms-of-Service acceptance audit log
export const acceptTos = (payload) => api.post("/tos/accept", payload);
export const listMyTosAcceptances = () => api.get("/tos/acceptances");


// V2.0.23 — Architect-ready Package zip (PDF + IFC + acceptance log)
export const requestArchitectPackage = (designId) =>
  api.post(`/designs/${designId}/architect-package`);

// V2.0.25 — Recently Deleted (tombstone) management
export const listTombstones = (designId) =>
  api.get(`/designs/${designId}/tombstones`);
export const restoreTombstone = (designId, { id, kind, floor_index }) =>
  api.post(`/designs/${designId}/tombstones/restore`, { id, kind, floor_index });

// V2.0.23 — Dashboard helper: fetch user's designs (most-recent first)
export const listMyDesigns = () => api.get("/designs");
